import clsx from "clsx";
import { useEffect, useMemo, useRef } from "react";
import { useController, type FieldPath, type FieldValues } from "react-hook-form";
import { useDispatch } from "react-redux";

import {
  setMotorSumInsuredLoading,
  setMotorSumInsuredSuccess,
} from "@/D2C/features/car-quote/redux/motor-suminsured/motorSumInsuredSlice";
import {
  formatCurrencyValue,
  getMotorSumInsuredRange,
  normalizeCurrencyInput,
  normalizeMotorSumInsuredResponse,
} from "@/D2C/features/car-quote/utils/motorSumInsured";
import { inputClassName } from "@/shared/components/TextInput";
import { WrapperInput } from "@/shared/components/WrapperInput";
import { axiosInstance } from "@/shared/lib/axiosInstance";
import { getThemeClass, getThemeComponentGroup } from "@/shared/lib/themeComponentClasses";
import type { AppDispatch } from "@/shared/redux/store/store";
import { usePosterReducers } from "@/shared/redux/store/useGetData";
import type { BaseInputProps } from "@/shared/types";
import { toTitleCaseUi } from "@/shared/utils/titleCase";

type MotorSumInsuredInputProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = BaseInputProps<TFieldValues, TName> & {
  apiEndpoint: string;
  isCallingApi?: boolean;
};

export function MotorMinMaxInput<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  placeholder = "Estimated car value",
  disabled,
  required,
  className,
  apiEndpoint,
  isCallingApi = false,
}: MotorSumInsuredInputProps<TFieldValues, TName>) {
  const dispatch = useDispatch<AppDispatch>();
  const { motorSumInsured: motorSumInsuredState } = usePosterReducers();
  const inputClasses = getThemeComponentGroup("input");
  const { field, fieldState } = useController({ control, name });
  const calledKeyRef = useRef<string | null>(null);
  const defaultSetRef = useRef(false);
  const placeholderText = toTitleCaseUi(placeholder);

  // ── Derived ───────────────────────────────────────────────────────────────
  const range = useMemo(
    () => getMotorSumInsuredRange(motorSumInsuredState.data),
    [motorSumInsuredState.data]
  );

  const description = useMemo(() => {
    if (range) {
      const base = `From AED ${formatCurrencyValue(range.min)} to AED ${formatCurrencyValue(range.max)}`;
      return motorSumInsuredState.source === "fallback"
        ? `${base} (demo data until API is live)`
        : base;
    }
    if (motorSumInsuredState.status === "loading") return "Loading insured value range...";
    return motorSumInsuredState.error ?? undefined;
  }, [range, motorSumInsuredState.source, motorSumInsuredState.status, motorSumInsuredState.error]);

  // ── Fetch once per endpoint ───────────────────────────────────────────────
  useEffect(() => {
    if (!isCallingApi) return;
    if (calledKeyRef.current === apiEndpoint) return;
    if (motorSumInsuredState.status === "loading" || motorSumInsuredState.status === "succeeded") {
      calledKeyRef.current = apiEndpoint;
      return;
    }

    calledKeyRef.current = apiEndpoint;

    let isMounted = true;

    const fetchRange = async () => {
      dispatch(setMotorSumInsuredLoading());
      try {
        const response = await axiosInstance.get(apiEndpoint);

        const payload =
          response?.data?.data &&
          typeof response.data.data === "object" &&
          !Array.isArray(response.data.data)
            ? response.data.data
            : response.data;

        const normalized = normalizeMotorSumInsuredResponse(payload);

        if (!Object.keys(normalized).length) {
          throw new Error("Empty insured value range from API.");
        }

        if (isMounted) {
          dispatch(setMotorSumInsuredSuccess({ data: normalized, source: "api" }));
        }
      } catch (err) {
        console.error("MotorMinMaxInput fetch error:", err);
      }
    };

    void fetchRange();
    return () => {
      isMounted = false;
    };
  }, [isCallingApi, apiEndpoint]);

  // ── Set Medium as default value (once, when API data arrives) ────────────
  useEffect(() => {
    const medium = motorSumInsuredState.data?.Medium;

    // Guard: no data, or already set, or user already typed something
    if (!medium || defaultSetRef.current || field.value) return;

    defaultSetRef.current = true;
    field.onChange(String(medium)); // ✅ field is accessible here via useController
  }, [motorSumInsuredState.data?.Medium]); // field.onChange is stable — safe to omit

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <WrapperInput
      label={label}
      error={fieldState.error?.message}
      description={description}
      required={required}
      className={className}
    >
      <div className="flex overflow-hidden rounded-xl">
        <div
          className={clsx(
            "flex h-9 min-w-[70px] items-center justify-center rounded-l-xl border px-2 text-[13px] font-semibold",
            getThemeClass(inputClasses.borderColor, "border-theme-border-strong"),
            getThemeClass(inputClasses.bgColor, "bg-theme-surface"),
            getThemeClass(inputClasses.textColor, "text-theme-text")
          )}
        >
          AED
        </div>

        <input
          ref={field.ref}
          name={field.name}
          value={formatCurrencyValue(field.value)}
          onBlur={field.onBlur}
          onChange={(e) => field.onChange(normalizeCurrencyInput(e.target.value))}
          inputMode="numeric"
          autoComplete="off"
          placeholder={placeholderText}
          disabled={disabled}
          className={clsx(
            inputClassName,
            "rounded-l-none border-l-0",
            getThemeClass(inputClasses.borderColor, "border-theme-border-strong"),
            getThemeClass(inputClasses.bgColor, "bg-theme-surface"),
            getThemeClass(inputClasses.textColor, "text-theme-text"),
            getThemeClass(inputClasses.placeholderColor, "placeholder:text-theme-text-placeholder"),
            getThemeClass(inputClasses.focusBorderColor, "focus:border-theme-primary"),
            "focus:ring-2",
            getThemeClass(inputClasses.focusRingColor, "focus:ring-theme-primary/10"),
            inputClasses.base,
            fieldState.error &&
              clsx(
                inputClasses.errorBorderColor || "border-theme-danger-bright",
                "focus:ring-theme-danger-bright/10"
              )
          )}
        />
      </div>
    </WrapperInput>
  );
}
